/**
 * 撤销重做上下文管理
 *
 * 这个文件负责管理工作流的撤销重做功能，包括：
 * - 历史状态管理
 * - 撤销操作
 * - 重做操作
 * - 快照管理
 * - 键盘快捷键支持
 */

import cloneDeep from 'lodash-es/cloneDeep'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Edge, Node, useReactFlow } from 'reactflow'
import { TabsContext } from './tabsContext'

/**
 * 撤销重做上下文类型定义
 */
type undoRedoContextType = {
  /** 撤销操作 */
  undo: () => void
  /** 重做操作 */
  redo: () => void
  /** 创建快照 */
  takeSnapshot: () => void
}

/**
 * 撤销重做选项类型
 */
type UseUndoRedoOptions = {
  /** 最大历史记录数量 */
  maxHistorySize: number
  /** 是否启用快捷键 */
  enableShortcuts: boolean
}

/**
 * 撤销重做 Hook 类型
 */
type UseUndoRedo = (options?: UseUndoRedoOptions) => {
  undo: () => void
  redo: () => void
  takeSnapshot: () => void
  canUndo: boolean
  canRedo: boolean
}

/**
 * 历史记录项类型
 */
type HistoryItem = {
  /** 节点列表 */
  nodes: Node[]
  /** 边列表 */
  edges: Edge[]
}

/**
 * 撤销重做上下文的初始值
 */
const initialValue = {
  undo: () => {},
  redo: () => {},
  takeSnapshot: () => {}
}

/**
 * 默认选项配置
 */
const defaultOptions: UseUndoRedoOptions = {
  maxHistorySize: 100,
  enableShortcuts: true
}

// 创建撤销重做上下文
export const undoRedoContext = createContext<undoRedoContextType>(initialValue)

/**
 * 撤销重做提供者组件
 *
 * @param children - 子组件
 * @returns 提供撤销重做相关状态和方法的上下文提供者
 */
export function UndoRedoProvider({ children }) {
  const { flow } = useContext(TabsContext)

  const [past, setPast] = useState<HistoryItem[]>([])
  const [future, setFuture] = useState<HistoryItem[]>([])

  useEffect(() => {
    // 每当流程变量发生变化时，需要清空过去和未来的状态
    setPast([])
    setFuture([])
  }, [flow?.id])

  // 通过 getNodes, getEdges 读取状态写入队列，通过 setNodes, setEdges 还原
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow()

  /**
   * 创建快照
   * 将当前图形状态推送到历史状态中
   */
  const takeSnapshot = useCallback(() => {
    // 将当前图形推送到过去状态
    setPast(old => {
      let newPast = cloneDeep(old)
      newPast = old.slice(old.length - defaultOptions.maxHistorySize + 1, old.length)
      newPast.push({ nodes: getNodes(), edges: getEdges() })
      return newPast
    })

    // 每当我们拍摄新快照时，需要清除重做操作以避免状态不匹配
    setFuture([])
  }, [getNodes, getEdges, past, future, flow, setPast, setFuture])

  /**
   * 撤销操作
   * 恢复到上一个状态
   */
  const undo = useCallback(() => {
    // 获取我们要返回的最后一个状态
    const pastState = past[past.length - 1]

    if (pastState) {
      // 首先从历史记录中移除状态
      setPast(old => {
        let newPast = cloneDeep(old)
        newPast = old.slice(0, old.length - 1)
        return newPast
      })
      // 我们存储当前图形用于重做操作
      setFuture(old => {
        let newFuture = cloneDeep(old)
        newFuture = old
        newFuture.push({ nodes: getNodes(), edges: getEdges() })
        return newFuture
      })
      // 保持相同的 ID
      pastState.nodes.forEach(el => {
        el.data.id = el.id
      })
      // 现在我们可以将图形设置为过去状态
      setNodes(pastState.nodes)
      setEdges(pastState.edges)
    }
  }, [setNodes, setEdges, getNodes, getEdges, future, past, setFuture, setPast])

  /**
   * 重做操作
   * 恢复到下一个状态
   */
  const redo = useCallback(() => {
    const futureState = future[future.length - 1]

    if (futureState) {
      setFuture(old => {
        let newFuture = cloneDeep(old)
        newFuture = old.slice(0, old.length - 1)
        return newFuture
      })
      setPast(old => {
        const newPast = cloneDeep(old)
        newPast.push({ nodes: getNodes(), edges: getEdges() })
        return newPast
      })
      // 保持相同的 ID
      futureState.nodes.forEach(el => {
        el.data.id = el.id
      })
      setNodes(futureState.nodes)
      setEdges(futureState.edges)
    }
  }, [future, past, setFuture, setPast, setNodes, setEdges, getNodes, getEdges, future])

  /**
   * 键盘快捷键事件处理
   * 支持 Ctrl+Z (撤销)、Ctrl+Y 或 Ctrl+Shift+Z (重做)
   */
  useEffect(() => {
    // 此效果用于附加全局事件处理器
    if (!defaultOptions.enableShortcuts) {
      return
    }

    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'z' && (event.ctrlKey || event.metaKey) && event.shiftKey) {
        // Ctrl+Shift+Z 或 Cmd+Shift+Z: 重做
        redo()
      } else if (event.key === 'y' && (event.ctrlKey || event.metaKey)) {
        // Ctrl+Y 或 Cmd+Y: 重做
        event.preventDefault() // 阻止默认操作
        redo()
      } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        // Ctrl+Z 或 Cmd+Z: 撤销
        undo()
      }
    }

    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [undo, redo])
  return (
    <undoRedoContext.Provider
      value={{
        undo,
        redo,
        takeSnapshot
      }}
    >
      {children}
    </undoRedoContext.Provider>
  )
}
